import { Combobox } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useGetAdditionalAlignersPatientsQuery } from "~/api";
import { ErrorMessage } from "~/components/ui/error-message";
import { Label, type LabelProps } from "~/components/ui/label";
import type { TPatient } from "~/types/patient";

type TPatientOption = { id: number; name: string };

export function PatientCombobox({
  selectedPatientId,
  onPatientIdChange,
  readOnly,
}: {
  selectedPatientId?: TPatient["patient_id"];
  onPatientIdChange(newPatientId?: TPatient["patient_id"]): void;
  readOnly?: boolean;
}) {
  const patientsQuery = useGetAdditionalAlignersPatientsQuery();
  const patients = patientsQuery.data ?? [];

  return (
    <PatientComboboxRoot
      patientId={selectedPatientId}
      onPatientIdChange={onPatientIdChange}
      patients={patients}
      isLoading={patientsQuery.isLoading}
      readOnly={readOnly}
      label={<FormattedMessage id="additional_aligners.patient.label" />}
      emptyDisplay={<FormattedMessage id="additional_aligners.patient.none" />}
      labelProps={{
        id: "validation-patient",
        required: true,
        tooltip: <FormattedMessage id="PAT_INFO_NAME_TOOLTIP" />,
      }}
      shadow
    />
  );
}

export function PatientComboboxRoot({
  patientId,
  onPatientIdChange,
  patients,
  isLoading,
  error,
  readOnly,
  emptyDisplay,
  labelProps,
  label,
  className,
  shadow,
  rounded,
}: {
  patientId?: TPatient["patient_id"];
  onPatientIdChange(newPatientId?: TPatient["patient_id"]): void;
  patients: TPatientOption[];
  isLoading?: boolean;
  error?: React.ReactChild;
  readOnly?: boolean;
  emptyDisplay?: React.ReactNode;
  label: React.ReactChild;
  labelProps?: Pick<LabelProps, "id" | "required" | "tooltip" | "semibold">;
  className?: string;
  shadow?: boolean;
  rounded?: boolean;
}) {
  const intl = useIntl();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState("");

  const selectedPatient = patients.find((patient) => patient.id == patientId) ?? null;
  const filteredPatients = filterPatients(patients, query);

  return (
    <div className={clsx("form-group", className)}>
      <Label {...labelProps} htmlFor="patient-input">
        {label}
      </Label>

      <div className={clsx("tw-relative tw-w-full", !rounded && "sm:tw-max-w-xs")}>
        <Combobox disabled={isLoading || readOnly} value={selectedPatient} onChange={(patient) => onPatientIdChange(patient?.id)} nullable>
          {({ open }) => (
            <>
              <div
                className={clsx(
                  "tw-relative tw-w-full tw-overflow-hidden tw-border tw-bg-white",
                  "tw-transition-all focus-within:tw-ring-1",
                  rounded ? "focus-within:tw-ring-ring" : "focus-within:tw-ring-blue-600",
                  { "tw-shadow-md": shadow, "tw-rounded-md": rounded },
                  error ? "tw-border-red-600" : rounded ? "tw-border-border enabled:hover:tw-border-ring" : "tw-border-[#c2cad8]  enabled:hover:tw-border-gray-400",
                )}
              >
                <Combobox.Input
                  id="patient-input"
                  autoComplete="off"
                  placeholder={intl.formatMessage({
                    id: isLoading ? "loading" : "additional_aligners.patient.placeholder",
                  })}
                  displayValue={(patient: TPatientOption | null) => patient?.name ?? ""}
                  className={clsx(
                    "tw-w-full tw-cursor-default tw-border-none tw-py-2 tw-pr-10",
                    "tw-text-base tw-outline-none placeholder:tw-text-gray-400 sm:tw-text-sm",
                    // Match `SelectTrigger`'s 10px left padding.
                    rounded ? "tw-pl-2.5 tw-text-foreground" : "tw-pl-3 tw-text-[#4d6b8a]",
                  )}
                  onChange={(event) => setQuery(event.target.value)}
                  onClick={() => {
                    if (!open) {
                      buttonRef.current?.click();
                    }
                  }}
                  onFocus={() => {
                    if (!open) {
                      buttonRef.current?.click();
                    }
                  }}
                />

                <Combobox.Button
                  ref={buttonRef}
                  className={clsx(
                    "tw-flex tw-cursor-default tw-items-center tw-border-none tw-transition-all",
                    // Match the shared `SelectTrigger` chevron and positioning:
                    // (down-only, not up-down), same positioning technique (centered via `top-1/2` +
                    // `-translate-y-1/2`, not a full-height `inset-y-0` button), and no background box —
                    // this button kept its legacy grey box (`#efefef`) and a different icon even in
                    // `rounded` mode, the visible mismatch between the two dropdowns.
                    rounded
                      ? "tw-absolute tw-right-0 tw-top-1/2 -tw-translate-y-1/2 tw-bg-transparent tw-pr-2"
                      : "tw-absolute tw-inset-y-0 tw-right-0 tw-bg-[#efefef] tw-pr-2 enabled:hover:tw-bg-[#e0e0e0]",
                  )}
                >
                  {rounded ? <ChevronDownIcon className="tw-size-5 tw-text-gray-400" aria-hidden="true" /> : <ChevronUpDownIcon className="tw-size-5 tw-text-gray-400" aria-hidden="true" />}
                </Combobox.Button>
              </div>

              <Combobox.Options
                className={clsx("tw-absolute tw-z-10 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-py-1 tw-pl-0", "tw-cursor-default tw-border tw-border-[#c2cad8] tw-bg-white tw-shadow-lg", {
                  "tw-rounded-md": rounded,
                })}
              >
                {filteredPatients == undefined ? (
                  <div className="tw-w-full tw-px-4 tw-py-2">
                    <FormattedMessage id="loading" />
                  </div>
                ) : patients.length == 0 ? (
                  <div className="tw-w-full tw-px-4 tw-py-2">{emptyDisplay}</div>
                ) : filteredPatients.length == 0 && query != "" ? (
                  <div className="tw-w-full tw-px-4 tw-py-2">
                    <FormattedMessage id="additional_aligners.patient.not_found" />
                  </div>
                ) : (
                  filteredPatients.map((patient) => (
                    <Combobox.Option
                      key={patient.id}
                      value={patient}
                      className={({ active }) =>
                        clsx("tw-relative tw-py-2 tw-pl-10 tw-pr-4", {
                          "tw-bg-brand-green tw-text-white": active,
                        })
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span className={clsx("tw-block tw-truncate", selected ? "tw-font-semibold" : "tw-font-normal")}>{`${patient.name} (${patient.id})`}</span>

                          {selected ? (
                            <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3">
                              <CheckIcon className={clsx("tw-size-5", active ? "tw-text-white" : "tw-text-brand-green")} aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </>
          )}
        </Combobox>
      </div>

      {error ? <ErrorMessage id={undefined}>{error}</ErrorMessage> : null}
    </div>
  );
}

function filterPatients(patients: TPatientOption[], query: string): TPatientOption[] {
  const lowerQuery = query.toLowerCase().trim();

  if (patients.length == 0 || lowerQuery == "") {
    return patients;
  }

  const filteredPatients = patients.filter((patient) => patient.name.toLowerCase().includes(lowerQuery) || patient.id.toString().toLowerCase().includes(lowerQuery));

  return filteredPatients;
}
